// API client utilities for The Architect - Vite compatible version
import {
    classifyError,
    createErrorResponse,
    isRetryable,
    getOverloadedService,
    getErrorMessage,
    ERROR_TYPES,
    processBackendError,
    classifyFrontendError
} from './errorHandler.js';
import { API_CONFIG as CENTRALIZED_CONFIG } from '../config/api.js';

const API_CONFIG = {
    baseURL: CENTRALIZED_CONFIG.baseURL, // Use centralized configuration
    defaultHeaders: {
        'Content-Type': 'application/json'
    },
    // Use v2 by default
    version: 'v2'
};

class ArchitectAPI {
    constructor() {
        this.config = API_CONFIG;
        this.currentUserId = null; // In-memory storage for authenticated user ID
        this.csrfToken = null; // CSRF token for state-changing requests
        this.csrfTokenExpiry = null; // Token expiration timestamp
        // 🔒 Device fingerprint now handled via httpOnly cookie (set by backend after OAuth)
    }

    /**
     * Fetch CSRF token from backend
     * Token is stored and reused until expiration
     * Returns null if user is not authenticated (401/403)
     */
    async fetchCSRFToken() {
        try {
            const response = await fetch(`${this.config.baseURL}/api/auth/csrf-token`, {
                method: 'GET',
                credentials: 'include',
                headers: this.config.defaultHeaders
            });

            if (response.ok) {
                const data = await response.json();
                this.csrfToken = data.csrfToken;
                // Set expiry 30 seconds before actual expiration for safety margin
                this.csrfTokenExpiry = Date.now() + ((data.expiresIn - 30) * 1000);
                return this.csrfToken;
            }
            // 401/403 means user is not authenticated - this is expected for guests
            // Don't log error, just return null silently
        } catch (error) {
            // CSRF fetch failed - will retry on next mutation
        }
        return null;
    }

    /**
     * Get valid CSRF token, fetching new one if expired
     */
    async getCSRFToken() {
        // Check if token exists and is not expired
        if (this.csrfToken && this.csrfTokenExpiry && Date.now() < this.csrfTokenExpiry) {
            return this.csrfToken;
        }
        // Fetch new token
        return this.fetchCSRFToken();
    }

    /**
     * Clear CSRF token (call on logout)
     */
    clearCSRFToken() {
        this.csrfToken = null;
        this.csrfTokenExpiry = null;
    }

    async request(endpoint, methodOrOptions = {}, data = null, additionalOptions = {}) {
        const url = `${this.config.baseURL}${endpoint}`;
        
        // Handle both calling patterns:
        // 1. request(endpoint, { method: 'POST', body: data })
        // 2. request(endpoint, 'POST', data, options)
        let options;
        if (typeof methodOrOptions === 'string') {
            // Pattern 2: method as string parameter
            options = {
                method: methodOrOptions,
                body: data ? JSON.stringify(data) : undefined,
                ...additionalOptions
            };
        } else {
            // Pattern 1: options object
            options = methodOrOptions;
        }
        
        // Build headers (authentication via httpOnly cookies)
        const headers = { ...this.config.defaultHeaders };

        // Add any additional headers from options
        Object.assign(headers, options.headers);

        // Add CSRF token for state-changing requests (POST, PUT, DELETE, PATCH)
        const method = (options.method || 'GET').toUpperCase();
        if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
            const csrfToken = await this.getCSRFToken();
            if (csrfToken) {
                headers['X-CSRF-Token'] = csrfToken;
            }
        }

        // 🔒 SECURITY: Device fingerprint now sent via httpOnly cookie (set by backend after OAuth)
        // No header needed - browser auto-sends device_fingerprint cookie with credentials: 'include'

        const config = {
            headers,
            credentials: 'include', // Use httpOnly cookies for authentication
            ...options
        };

        // Add timeout to prevent hanging - AI calls can take up to 5 minutes
        const timeout = options.timeout || 300000; // 5 minutes default for AI operations
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        try {
            const response = await fetch(url, {
                ...config,
                credentials: 'include', // Include cookies for CORS
                signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            
            if (!response.ok) {
                // Try to get backend error details
                let errorData = null;
                let finalError = null;

                try {
                    errorData = await response.json();

                    // Check for session expiration (401/403 with session-related message)
                    const isSessionError = (response.status === 401 || response.status === 403) &&
                        errorData?.error &&
                        (errorData.error.toLowerCase().includes('session') ||
                         errorData.error.toLowerCase().includes('expired') ||
                         errorData.error.toLowerCase().includes('invalid or expired'));

                    if (isSessionError) {
                        // Emit session expired event for AuthContext to handle
                        if (typeof window !== 'undefined') {
                            window.dispatchEvent(new CustomEvent('session_expired', {
                                detail: {
                                    message: errorData.error,
                                    status: response.status,
                                    timestamp: Date.now()
                                }
                            }));
                        }

                        // Create session expired error
                        finalError = new Error(errorData.error || 'Your session has expired');
                        finalError.type = 'session_expired';
                        finalError.errorType = ERROR_TYPES.SESSION_EXPIRED;
                        finalError.isRetryable = false;
                        finalError.status = response.status;
                        finalError._processedByErrorHandler = true;
                        finalError.userMessage = 'Your session has expired. Please sign in again.';
                        throw finalError;
                    }
                    
                    // Special handling for 402 Payment Required (insufficient credits)
                    if (response.status === 402 || errorData?.error === 'insufficient_credits') {
                        finalError = new Error(errorData?.message || 'Insufficient credits to complete this operation');
                        finalError.type = 'insufficient_credits';
                        finalError.isRetryable = false;
                        finalError.status = 402;
                        finalError.required = errorData?.required || 0;
                        finalError.available = errorData?.available || 0;
                        finalError.shortfall = errorData?.shortfall || 0;
                        finalError.breakdown = errorData?.breakdown || null;
                        finalError.suggestedAction = errorData?.suggestedAction || 'purchase_credits';
                        finalError._processedByErrorHandler = true;
                        finalError.userMessage = `You need ${errorData?.required || 'more'} credits to complete this operation. You currently have ${errorData?.available || 0} credits available.`;
                        
                    }
                    // Try to process enhanced backend error format first (matching original API pattern)
                    else if (processBackendError) {
                        const enhancedError = processBackendError(errorData, endpoint);
                        if (enhancedError) {
                            // Use enhanced backend error directly and set the expected flag
                            enhancedError._processedByErrorHandler = true;
                            // Create a classification object for frontend compatibility
                            enhancedError.classification = {
                                type: enhancedError.errorType,
                                isRetryable: enhancedError.isRetryable,
                                service: enhancedError.service || 'AI Service',
                                category: enhancedError.isRetryable ? 'retryable' : 'non_retryable'
                            };
                            finalError = enhancedError;
                        }
                    }
                    
                    if (!finalError) {
                        // Fallback to basic error processing
                        finalError = new Error(errorData?.error || `HTTP ${response.status}: ${response.statusText}`);
                        
                        // Preserve any backend error details
                        if (errorData?.errorType) finalError.errorType = errorData.errorType;
                        if (errorData?.isRetryable !== undefined) finalError.isRetryable = errorData.isRetryable;
                        if (errorData?.service) finalError.service = errorData.service;
                        if (errorData?.step) finalError.step = errorData.step;
                        if (errorData?.platform) finalError.platform = errorData.platform;
                    }
                    
                } catch (parseError) {
                    // Backend returned non-JSON error
                    finalError = new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                
                // If not already enhanced, process through centralized error handler
                if (!finalError._isEnhancedBackendError && !finalError._processedByErrorHandler) {
                    finalError = this.processError(finalError, endpoint, errorData);
                }
                
                throw finalError;
            }

            const responseData = await response.json();
            
            // Track credit usage if response includes cost information
            if (responseData.cost && responseData.cost.totalCost > 0) {
                this.trackCreditUsage(endpoint, responseData.cost, responseData.model);
            }
            
            return responseData;
        } catch (error) {
            clearTimeout(timeoutId);
            
            // Handle timeout specifically
            if (error.name === 'AbortError') {
                const timeoutError = new Error(`Request timeout - the server is taking too long to respond. Please try again.`);
                throw this.processError(timeoutError, endpoint);
            }
            
            // If already processed by error handler, re-throw as-is
            if (error._processedByErrorHandler) {
                throw error;
            }
            
            // Process other errors through centralized error handler
            
            throw this.processError(error, endpoint);
        }
    }

    /**
     * Track credit usage and emit events for real-time tracking
     * @param {string} endpoint - The API endpoint used
     * @param {Object} cost - Cost information from response
     * @param {string} model - Model used for the operation
     */
    trackCreditUsage(endpoint, cost, model) {
        const operation = this.extractOperationFromEndpoint(endpoint);
        const credits = Math.round(cost.totalCost || 0);
        
        // Emit credit usage event for real-time tracking
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('creditUsage', {
                detail: {
                    credits,
                    operation,
                    model,
                    endpoint,
                    timestamp: Date.now()
                }
            }));

            // Trigger credit refresh for CreditManager
            window.dispatchEvent(new CustomEvent('api_operation', {
                detail: {
                    type: 'credit_operation',
                    operation,
                    credits,
                    endpoint
                }
            }));

        }
    }

    /**
     * Extract operation name from API endpoint for display
     * @param {string} endpoint - API endpoint
     * @returns {string} Operation name
     */
    extractOperationFromEndpoint(endpoint) {
        if (endpoint.includes('/research')) return 'Research';
        if (endpoint.includes('/validate')) return 'Validation';
        if (endpoint.includes('/analysis')) return 'Analysis';
        if (endpoint.includes('/prompts')) return 'Prompts';
        if (endpoint.includes('/flow-diagram')) return 'Flow Diagram';
        return 'API Operation';
    }

    /**
     * Process errors through centralized error handler
     * @param {Error} error - The error to process
     * @param {string} endpoint - The API endpoint that failed
     * @param {Object} backendErrorData - Additional error data from backend
     * @returns {Error} Processed error with classification
     */
    processError(error, endpoint, backendErrorData = null) {
        // Extract step and platform from endpoint
        const step = this.extractStepFromEndpoint(endpoint);
        const platform = backendErrorData?.platform || 'unknown';
        
        // Classify the error using centralized handler
        const classification = classifyError(error);
        
        // Create enhanced error with classification details
        const enhancedError = new Error(error.message);
        enhancedError.originalError = error;
        enhancedError.classification = classification;
        enhancedError.step = step;
        enhancedError.platform = platform;
        enhancedError.endpoint = endpoint;
        enhancedError.isRetryable = classification.isRetryable;
        enhancedError.service = classification.service;
        enhancedError.errorType = classification.type;
        enhancedError.userMessage = getErrorMessage(classification.type, classification.service, error.message).user;
        enhancedError._processedByErrorHandler = true;
        
        // Add backend error details if available
        if (backendErrorData) {
            enhancedError.backendData = backendErrorData;
            // Preserve backend retry info if available
            if (backendErrorData.isRetryable !== undefined) {
                enhancedError.isRetryable = backendErrorData.isRetryable;
            }
        }
        
        
        return enhancedError;
    }

    /**
     * Extract step name from API endpoint
     * @param {string} endpoint - API endpoint
     * @returns {string} Step name
     */
    extractStepFromEndpoint(endpoint) {
        if (endpoint.includes('/research')) return 'research';
        if (endpoint.includes('/validate')) return 'validation';
        if (endpoint.includes('/analysis')) return 'analysis';
        if (endpoint.includes('/prompts')) return 'prompts';
        if (endpoint.includes('/session')) return 'session';
        return 'api';
    }

    // Production mode - debug helpers removed

    // ========== V2 SIMPLIFIED FLOW APIs - PRESERVED ==========

    async createSession(idea, platform, options = {}) {
        const payload = { 
            idea, 
            platform, 
            force: options.force || false, // Allow forcing new session creation
            enableWebSearch: options.enableWebSearch ?? true,
            answers: options.answers || [] // Include answers in session creation
        };
        
        return this.request('/api/v2/session', {
            method: 'POST',
            body: JSON.stringify(payload),
            timeout: 60000 // 1 minute timeout for session creation
        });
    }

    async getSession(sessionId) {
        return this.request(`/api/v2/session?id=${sessionId}`);
    }

    // Session reuse management methods
    async getActiveSession() {
        return this.request('/api/v2/session/active', {
            method: 'GET',
            timeout: 10000 // Quick check for active sessions
        });
    }

    async abandonSession(sessionId) {
        return this.request('/api/v2/session/abandon', {
            method: 'POST',
            body: JSON.stringify({ sessionId }),
            timeout: 10000
        });
    }

    async validateSession(sessionId) {
        // This could be part of getSession or a separate endpoint
        // For now, use getSession to validate
        try {
            const session = await this.getSession(sessionId);
            return { canResume: !!session.session, session: session.session };
        } catch (error) {
            return { canResume: false, error: error.message };
        }
    }

    async updateSession(sessionId, step, data) {
        return this.request('/api/v2/session', {
            method: 'PUT',
            body: JSON.stringify({ sessionId, step, data })
        });
    }

    async runResearch(idea, answers = [], platform = null, enableWebSearch = true, options = {}) { // Default to true for better results
        // Production mode - mock data handling removed
        const payload = { 
            idea,
            answers, // Send answers to backend for free-tier analysis
            platform, // Add platform parameter
            enableWebSearch // Add web search setting
        };

        // Make initial request to start research
        const response = await this.request('/api/v2/research', {
            method: 'POST',
            body: JSON.stringify(payload),
            timeout: 300000 // 5 minutes for AI research
        });

        // Check if we got a polling response (task started or in progress)
        if (response.status === 'started' || response.status === 'in_progress') {
            
            // Start polling for completion
            return this.pollForResearchCompletion(
                response.taskId, 
                response.agentId, 
                options
            );
        }
        
        // Otherwise return the response as-is (backward compatibility)
        return response;
    }

    async runValidation(research, originalIdea, answers = [], platform = null, options = {}) {
        // Production mode - mock data handling removed
        const payload = { 
            research, 
            originalIdea,
            answers,
            platform // Add platform parameter
        };

        // Make initial request to start validation
        const response = await this.request('/api/v2/validate', {
            method: 'POST',
            body: JSON.stringify(payload),
            timeout: 300000 // 5 minutes for AI validation
        });

        // Check if we got a polling response (task started or in progress)
        if (response.status === 'started' || response.status === 'in_progress') {
            
            // Start polling for completion
            return this.pollForValidationCompletion(
                response.taskId, 
                response.agentId, 
                options
            );
        }
        
        // Otherwise return the response as-is (backward compatibility)
        return response;
    }

    async runAnalysis(research, answers, originalIdea, platform = null, validation = null, options = {}) {
        // Updated for new 4-step system: includes validation data for LLD generation
        const payload = { 
            research, 
            validation, // Include validation data from step 2
            answers, 
            originalIdea,
            platform // Add platform parameter
        };

        // Make initial request to start analysis
        const response = await this.request('/api/v2/analysis', {
            method: 'POST',
            body: JSON.stringify(payload),
            timeout: 300000 // 5 minutes for AI analysis
        });

        // Check if we got a polling response (task started or in progress)
        if (response.status === 'started' || response.status === 'in_progress') {
            
            // Start polling for completion
            return this.pollForAnalysisCompletion(
                response.taskId, 
                response.agentId, 
                options
            );
        }
        
        // Otherwise return the response as-is (backward compatibility)
        return response;
    }

    async generatePrompts(promptsPayload) {
        return this.request('/api/v2/prompts', {
            method: 'POST',
            body: JSON.stringify(promptsPayload),
            timeout: 300000 // 5 minutes for AI prompts generation
        });
    }

    // V1 API methods removed - application now uses V2 exclusively

    // ========== UTILITY METHODS ==========

    setVersion(version) {
        this.config.version = version;
    }

    async getQuestions(sessionId) {
        // In v2, questions come from validation step
        const session = await this.getSession(sessionId);
        return session.validation?.questions || [];
    }

    // Export/Download utilities
    async exportPrompts(sessionId, format = 'json') {
        const response = await fetch(`/api/v2/prompts?session=${sessionId}&format=export&exportFormat=${format}`, {
            credentials: 'include'
        });
        
        if (!response.ok) {
            throw new Error('Export failed');
        }

        return response.blob();
    }

    downloadBlob(blob, filename) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }

    // ========== CREDIT & BILLING APIs ==========

    async getCreditBalance() {
        return this.request(`/api/v2/credits/balance`, {
            method: 'GET',
            timeout: 30000
        });
    }

    /**
     * Get pricing configuration from backend (single source of truth)
     * Returns plans, workflow costs, bonuses, and model pricing
     */
    async getPricing() {
        return this.request(`/api/v2/pricing`, {
            method: 'GET',
            timeout: 10000
        });
    }


    async getCreditHistory(options = {}) {
        const params = new URLSearchParams(
            Object.fromEntries(Object.entries(options).filter(([_, v]) => v != null))
        );
        
        return this.request(`/api/v2/credits/history?${params}`, {
            method: 'GET',
            timeout: 30000
        });
    }

    async upgradeSubscription(newPlanId, paymentMethodId, email, reason = 'user_upgrade', billingDetails = {}) {
        return this.request('/api/v2/subscription/upgrade', {
            method: 'POST',
            body: JSON.stringify({
                newPlanId,
                paymentMethodId,
                email,
                reason,
                billingDetails
            }),
            timeout: 60000
        });
    }

    async getBillingInvoices(options = {}) {
        const params = new URLSearchParams(
            Object.fromEntries(Object.entries(options).filter(([_, v]) => v != null))
        );
        
        return this.request(`/api/v2/billing/invoices?${params}`, {
            method: 'GET',
            timeout: 30000
        });
    }

    async getCreditAnalytics(options = {}) {
        const params = new URLSearchParams({
            timeframe: options.timeframe || 'weekly',
            includeComparison: options.includeComparison !== false,
            includePredictions: options.includePredictions !== false,
            includeFraudCheck: options.includeFraudCheck !== false,
            ...Object.fromEntries(Object.entries(options).filter(([k, v]) => 
                !['timeframe', 'includeComparison', 'includePredictions', 'includeFraudCheck'].includes(k) && v != null
            ))
        });
        
        return this.request(`/api/v2/credits/analytics?${params}`, {
            method: 'GET',
            timeout: 45000
        });
    }

    // ========== USER MANAGEMENT ==========

    async getUserProfile() {
        // Use the credit balance endpoint which includes comprehensive user info
        return this.getCreditBalance();
    }

    async getUserSessions(options = {}) {
        const params = new URLSearchParams({
            limit: options.limit || 20,
            ...Object.fromEntries(Object.entries(options).filter(([k, v]) => k !== 'limit' && v != null))
        });
        
        // This would typically be a dedicated endpoint, but for now we can use session data
        // from the credit analytics which includes recent activity
        return this.getCreditAnalytics({ timeframe: 'weekly' });
    }

    // ========== REFERRAL SYSTEM ==========

    /**
     * Get user's referral code (creates one if doesn't exist)
     */
    async getReferralCode() {
        return this.request('/api/v2/referrals/code', {
            method: 'GET',
            timeout: 15000
        });
    }

    /**
     * Regenerate user's referral code (invalidates old one)
     */
    async regenerateReferralCode() {
        return this.request('/api/v2/referrals/regenerate', {
            method: 'POST',
            timeout: 15000
        });
    }

    /**
     * Apply a referral code
     * @param {string} referralCode - The referral code to apply
     */
    async applyReferralCode(referralCode) {
        return this.request('/api/v2/referrals/apply', {
            method: 'POST',
            body: JSON.stringify({ referralCode }),
            timeout: 15000
        });
    }

    /**
     * Get user's referral statistics
     */
    async getReferralStats() {
        return this.request('/api/v2/referrals/stats', {
            method: 'GET',
            timeout: 15000
        });
    }

    /**
     * Get user's referral history
     * @param {number} limit - Max results to return
     */
    async getReferralHistory(limit = 10) {
        const params = new URLSearchParams({ limit: limit.toString() });
        return this.request(`/api/v2/referrals/history?${params}`, {
            method: 'GET',
            timeout: 15000
        });
    }

    // ========== SECURITY (Session/Device Management) ==========

    /**
     * Get user's active sessions
     */
    async getSecuritySessions() {
        return this.request('/api/v2/security/sessions', {
            method: 'GET',
            timeout: 15000
        });
    }

    /**
     * Revoke a specific session
     * @param {string} sessionId - Session ID to revoke
     */
    async revokeSession(sessionId) {
        return this.request(`/api/v2/security/sessions/${sessionId}`, {
            method: 'DELETE',
            timeout: 15000
        });
    }

    /**
     * Revoke all sessions except current ("Sign out everywhere")
     */
    async revokeAllSessions() {
        return this.request('/api/v2/security/sessions', {
            method: 'DELETE',
            timeout: 15000
        });
    }

    /**
     * Get user's remembered devices
     */
    async getSecurityDevices() {
        return this.request('/api/v2/security/devices', {
            method: 'GET',
            timeout: 15000
        });
    }

    /**
     * Remove (block) a device
     * @param {string} deviceId - Device ID to remove
     */
    async removeDevice(deviceId) {
        return this.request(`/api/v2/security/devices/${deviceId}`, {
            method: 'DELETE',
            timeout: 15000
        });
    }

    // ========== PLANS & PRICING ==========

    async getAvailablePlans() {
        return this.request('/api/v2/plans', {
            method: 'GET',
            timeout: 30000
        });
    }

    // ========== ENHANCED API CALLS WITH USER ID ==========

    async runResearchWithUserId(idea, answers = [], platform = null, enableWebSearch = true, userId = null, options = {}) { // Default to true for better results
        const payload = { 
            idea,
            answers,
            platform,
            enableWebSearch,
            userId // Add userId for credit tracking
        };

        // Make initial request to start research
        const response = await this.request('/api/v2/research', {
            method: 'POST',
            body: JSON.stringify(payload),
            timeout: 300000
        });


        // Check if we got a polling response (task started or in progress)
        if (response.status === 'started' || response.status === 'in_progress') {
            
            // Start polling for completion
            return this.pollForResearchCompletion(
                response.taskId, 
                response.agentId, 
                options
            );
        }
        
        // Otherwise return the response as-is (backward compatibility)
        return response;
    }

    async runValidationWithUserId(research, originalIdea, answers = [], platform = null, userId = null, options = {}) {
        const payload = { 
            research, 
            originalIdea,
            answers,
            platform,
            userId // Add userId for credit tracking
        };

        // Make initial request to start validation
        const response = await this.request('/api/v2/validate', {
            method: 'POST',
            body: JSON.stringify(payload),
            timeout: 300000
        });


        // Check if we got a polling response (task started or in progress)
        if (response.status === 'started' || response.status === 'in_progress') {
            
            // Start polling for completion
            return this.pollForValidationCompletion(
                response.taskId, 
                response.agentId, 
                options
            );
        }
        
        // Otherwise return the response as-is (backward compatibility)
        return response;
    }

    async runAnalysisWithUserId(research, answers, originalIdea, platform = null, userId = null, validation = null, options = {}) {
        const payload = { 
            research, 
            validation, // Include validation data from step 2
            answers, 
            originalIdea,
            platform,
            userId // Add userId for credit tracking
        };

        // Make initial request to start analysis
        const response = await this.request('/api/v2/analysis', {
            method: 'POST',
            body: JSON.stringify(payload),
            timeout: 300000
        });

        // Check if we got a polling response (task started or in progress)
        if (response.status === 'started' || response.status === 'in_progress') {
            
            // Start polling for completion
            return this.pollForAnalysisCompletion(
                response.taskId, 
                response.agentId, 
                options
            );
        }
        
        // Otherwise return the response as-is (backward compatibility)
        return response;
    }

    async generatePromptsWithUserId(promptsPayload, userId = null, options = {}) {
        const payload = {
            ...promptsPayload,
            userId, // Add userId for credit tracking
            enableBatchUpdates: true // Signal backend to send batch updates
        };

        // Make initial request to start generation
        const response = await this.request('/api/v2/prompts', {
            method: 'POST',
            body: JSON.stringify(payload),
            timeout: 300000
        });
        
        // Check if we got a polling response (task started or in progress)
        if (response.status === 'started' || response.status === 'in_progress') {
            
            // Start polling for completion
            return this.pollForPromptCompletion(
                response.taskId, 
                response.agentId, 
                options
            );
        }
        
        // Otherwise return the response as-is (backward compatibility)
        return response;
    }
    
    // Check prompt generation status
    async checkPromptGenerationStatus(taskId, agentId) {
        return this.request(`/api/v2/prompts/status?taskId=${taskId}&agentId=${agentId}`, {
            method: 'GET',
            timeout: 10000
        });
    }

    // Check research status
    async checkResearchStatus(taskId, agentId) {
        return this.request(`/api/v2/research/status?taskId=${taskId}&agentId=${agentId}`, {
            method: 'GET',
            timeout: 10000
        });
    }

    // Check validation status
    async checkValidationStatus(taskId, agentId) {
        return this.request(`/api/v2/validation/status?taskId=${taskId}&agentId=${agentId}`, {
            method: 'GET',
            timeout: 10000
        });
    }

    // Check analysis status
    async checkAnalysisStatus(taskId, agentId) {
        return this.request(`/api/v2/analysis/status?taskId=${taskId}&agentId=${agentId}`, {
            method: 'GET',
            timeout: 10000
        });
    }
    
    // Poll for research completion
    async pollForResearchCompletion(taskId, agentId, options = {}) {
        const maxAttempts = options.maxAttempts || 150; // Max 5 minutes of polling
        const pollInterval = options.pollInterval || 2000; // 2 seconds
        let attempts = 0;
        
        
        while (attempts < maxAttempts) {
            // Wait before polling (except on first attempt)
            if (attempts > 0) {
                await new Promise(resolve => setTimeout(resolve, pollInterval));
            }
            
            try {
                const status = await this.checkResearchStatus(taskId, agentId);
                
                // Call progress callback if provided
                if (options.onProgress) {
                    options.onProgress(status);
                }
                
                // Check if completed
                if (status.status === 'completed') {
                    return status;
                }
                
                // Check if failed
                if (status.status === 'failed' || status.status === 'error') {
                    throw new Error(status.error || 'Research task failed');
                }
                
                attempts++;
            } catch (error) {
                if (!isRetryable(error) || attempts >= maxAttempts - 1) {
                    throw error;
                }
                
                attempts++;
            }
        }
        
        throw new Error(`Research task ${taskId} timed out after ${maxAttempts} attempts`);
    }

    // Poll for validation completion
    async pollForValidationCompletion(taskId, agentId, options = {}) {
        const maxAttempts = options.maxAttempts || 150; // Max 5 minutes of polling
        const pollInterval = options.pollInterval || 2000; // 2 seconds
        let attempts = 0;
        
        
        while (attempts < maxAttempts) {
            // Wait before polling (except on first attempt)
            if (attempts > 0) {
                await new Promise(resolve => setTimeout(resolve, pollInterval));
            }
            
            try {
                const status = await this.checkValidationStatus(taskId, agentId);
                
                // Call progress callback if provided
                if (options.onProgress) {
                    options.onProgress(status);
                }
                
                // Check if completed
                if (status.status === 'completed') {
                    return status;
                }
                
                // Check if failed
                if (status.status === 'failed' || status.status === 'error') {
                    throw new Error(status.error || 'Validation task failed');
                }
                
                attempts++;
            } catch (error) {
                if (!isRetryable(error) || attempts >= maxAttempts - 1) {
                    throw error;
                }
                
                attempts++;
            }
        }
        
        throw new Error(`Validation task ${taskId} timed out after ${maxAttempts} attempts`);
    }

    // Poll for analysis completion
    async pollForAnalysisCompletion(taskId, agentId, options = {}) {
        const maxAttempts = options.maxAttempts || 150; // Max 5 minutes of polling
        const pollInterval = options.pollInterval || 2000; // 2 seconds
        let attempts = 0;
        
        
        while (attempts < maxAttempts) {
            // Wait before polling (except on first attempt)
            if (attempts > 0) {
                await new Promise(resolve => setTimeout(resolve, pollInterval));
            }
            
            try {
                const status = await this.checkAnalysisStatus(taskId, agentId);
                
                // Call progress callback if provided
                if (options.onProgress) {
                    options.onProgress(status);
                }
                
                // Check if completed
                if (status.status === 'completed') {
                    return status;
                }
                
                // Check if failed
                if (status.status === 'failed' || status.status === 'error') {
                    throw new Error(status.error || 'Analysis task failed');
                }
                
                attempts++;
            } catch (error) {
                if (!isRetryable(error) || attempts >= maxAttempts - 1) {
                    throw error;
                }
                
                attempts++;
            }
        }
        
        throw new Error(`Analysis task ${taskId} timed out after ${maxAttempts} attempts`);
    }

    // Poll for prompt completion
    async pollForPromptCompletion(taskId, agentId, options = {}) {
        const maxAttempts = options.maxAttempts || 300; // Max 10 minutes of polling (increased for batch generation)
        const pollInterval = options.pollInterval || 2000; // 2 seconds
        let attempts = 0;
        
        
        while (attempts < maxAttempts) {
            // Wait before polling (except on first attempt)
            if (attempts > 0) {
                await new Promise(resolve => setTimeout(resolve, pollInterval));
            }
            
            try {
                const status = await this.checkPromptGenerationStatus(taskId, agentId);
                
                // Call progress callback if provided
                if (options.onBatchProgress && status.progress) {
                    options.onBatchProgress({
                        batchIndex: status.progress.currentBatch,
                        totalCompleted: status.progress.completedPrompts,
                        totalBatches: status.progress.totalBatches,
                        targetPrompts: status.progress.targetPrompts,
                        progress: status.progress.percentComplete,
                        progressPercent: status.progress.percentComplete,
                        estimatedTimeRemaining: status.estimatedTimeRemaining
                    });
                }
                
                // Check if completed
                if (status.status === 'completed' && status.prompts) {
                    return status;
                }
                
                // Check if failed
                if (status.status === 'failed') {
                    throw new Error(`Prompt generation failed: ${status.error || 'Unknown error'}`);
                }
                
            } catch (error) {
                // If it's a final attempt, throw the error
                if (attempts >= maxAttempts - 1) {
                    throw error;
                }
                
                // Otherwise, continue polling (might be temporary network issue)
            }
            
            attempts++;
        }
        
        throw new Error('Prompt generation timed out after maximum polling attempts');
    }

    async generateFlowDiagramWithUserId(analysisData, sessionId, userId = null, force = false) {
        const payload = {
            analysisData,
            sessionId,
            userId, // Add userId for credit tracking
            force // Add force parameter to bypass cache
        };

        return this.request('/api/v2/flow-diagram', {
            method: 'POST',
            body: JSON.stringify(payload),
            timeout: 300000
        });
    }

    // ========== USER AUTHENTICATION & SESSION MANAGEMENT ==========

    getCurrentUserId() {
        // Return user ID stored in memory (set by AuthContext after authentication)
        return this.currentUserId || null;
    }

    setCurrentUserId(userId) {
        // Store user ID in memory (called by AuthContext after authentication)
        this.currentUserId = userId;
    }

    clearCurrentUser() {
        try {
            // Clear in-memory user ID
            this.currentUserId = null;

            // Clear CSRF token
            this.clearCSRFToken();

            // Clear session storage (keep for session data)
            this.clearCurrentSession();
        } catch (error) {
        }
    }

    // Session management
    getCurrentSessionId() {
        return sessionStorage.getItem('currentSessionId');
    }

    setCurrentSessionId(sessionId) {
        sessionStorage.setItem('currentSessionId', sessionId);
    }

    clearCurrentSession() {
        try {
            // Clear specific session items
            sessionStorage.removeItem('currentSessionId');

            // Clear any other session-related items that might exist
            const keysToRemove = [];
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && (key.startsWith('architect_') || key.startsWith('session_'))) {
                    keysToRemove.push(key);
                }
            }

            keysToRemove.forEach(key => {
                sessionStorage.removeItem(key);
            });
        } catch (error) {
        }
    }
}

// Create instance
const api = new ArchitectAPI();

// V1 legacy function exports removed - use api instance methods directly

export const handleApiError = (error, defaultMessage = 'An error occurred') => {
    // If error is already processed by our centralized handler, use it directly
    if (error._processedByErrorHandler) {
        const message = error.userMessage || error.message || defaultMessage;
        
        // Use the global toast function if available
        if (typeof window.showToast === 'function') {
            window.showToast(message, 'error');
        } else {
            // Error message would be logged here
        }
        
        return {
            message,
            details: error,
            classification: error.classification,
            isRetryable: error.isRetryable,
            service: error.service,
            errorType: error.errorType,
            step: error.step,
            platform: error.platform
        };
    }
    
    // For legacy errors, process through centralized handler
    const classification = classifyError(error);
    const errorMessage = getErrorMessage(classification.type, classification.service, error.message);
    
    let message = errorMessage.user || defaultMessage;
    if (error?.message) {
        message = error.message;
    } else if (typeof error === 'string') {
        message = error;
    }
    
    // Use the global toast function if available
    if (typeof window.showToast === 'function') {
        window.showToast(message, 'error');
    }
    
    return {
        message,
        details: error,
        classification: classification,
        isRetryable: classification.isRetryable,
        service: classification.service,
        errorType: classification.type,
        step: 'unknown',
        platform: 'unknown'
    };
};

export const showToast = (message, type = 'info') => {
    // Use the global toast function if available
    if (typeof window.showToast === 'function') {
        window.showToast(message, type);
    }
};

// Export the main API class and instance
export { ArchitectAPI };
export default api; 